import { Text, View } from "react-native";

import type { Exercise, PendingMaxBumps } from "@jackedlogs/shared";

import { useExerciseNameBySlug } from "../Logger/exerciseName";
import { Button } from "../primitives/Button";
import type { TFn } from "../i18nTypes";
import type { UiTheme } from "../theme";
import { makeStyles } from "./maxBumpBannerStyles";

// Presentational MAX-bump banner: after a workout save, offers Confirm/
// Dismiss (raises) or Bad-day/Keep/Confirm (regressions) per candidate
// exercise. Lifted from mobile's WorkoutsListScreen (Track R/R-J) into
// @jackedlogs/ui so web-rnw's WorkoutsScreen mounts the SAME component —
// state + handlers stay host-owned (useMaxBumpFlow on mobile,
// useMaxBumpFlowWeb on web-rnw) and arrive via props; this only renders.
// Renders nothing when there are no pending candidates.
//
// Rebuilt on the shared <Button> primitive instead of hand-rolled
// Pressable+StyleSheet (next/consolidation.md CN-strip-banner names this
// file as one of 5 copies of that pattern). NOT collapsed onto <CuePill>
// (the other candidate primitive CN-strip-banner points at): CuePill
// hardcodes variant="secondary" for every action, which would flatten
// Confirm's primary/filled visual weight, and the regression row needs a
// THIRD action past CuePill's documented 2-action design intent. Both are
// design calls, not lift decisions — registered in consolidation.md rather
// than decided here.
export function MaxBumpBanner({
  pendingMaxBumps,
  catalog,
  maxBumpBusy,
  onConfirmMaxBump,
  onDismissMaxBump,
  onSnoozeMaxBump,
  theme,
  t,
}: {
  pendingMaxBumps: PendingMaxBumps | null;
  catalog: readonly Exercise[] | null;
  maxBumpBusy: ReadonlySet<string>;
  onConfirmMaxBump: (
    exerciseId: string,
    suggestedKg: number,
    direction?: "raise" | "lower",
  ) => void;
  onDismissMaxBump: (exerciseId: string) => void;
  onSnoozeMaxBump: (exerciseId: string) => void;
  theme: UiTheme;
  t: TFn;
}) {
  const styles = makeStyles(theme);
  // Resolve the exercise name via the catalog FK → localized string. Reading
  // `ex.name` raw renders `[object Object]` since the schema flipped name to
  // LocalizedText (shared 1.95.0) — same bug class as the v2.52.0 rest-picker.
  // Hook runs before the early return (rules-of-hooks).
  const exerciseNameOf = useExerciseNameBySlug(catalog);
  if (pendingMaxBumps === null) return null;
  return (
    <View style={styles.wrap}>
      <Text style={styles.header}>
        {t("mobile.workouts.maxBumpHeader", {
          count: pendingMaxBumps.candidates.length,
        })}
      </Text>
      {pendingMaxBumps.candidates.map((c) => {
        const exName = exerciseNameOf(c.exerciseId, c.exerciseId);
        const busy = maxBumpBusy.has(c.exerciseId);
        // Bidirectional MAX (next/coaching.md §9): a 'lower' candidate is
        // a regression prompt — different copy + a third "bad day" action
        // that snoozes instead of writing. Raises keep the 2-button shape.
        const isLower = c.direction === "lower";
        return (
          <View key={c.exerciseId} style={styles.row}>
            <Text style={styles.label} numberOfLines={3}>
              {isLower
                ? t("mobile.workouts.maxDropRow", {
                    exercise: exName,
                    valueKg: c.suggestion.suggestedKg,
                    currentKg: c.regression?.currentMaxKg ?? 0,
                    weeks: c.regression?.weeksSinceMax ?? 0,
                  })
                : t("mobile.workouts.maxBumpRow", {
                    exercise: exName,
                    valueKg: c.suggestion.suggestedKg,
                  })}
            </Text>
            <View style={styles.actions}>
              {isLower ? (
                <Button
                  theme={theme}
                  compact
                  variant="ghost"
                  label={t("mobile.workouts.maxDropBadDay")}
                  onPress={() => onSnoozeMaxBump(c.exerciseId)}
                  disabled={busy}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 2 }}
                />
              ) : null}
              <Button
                theme={theme}
                compact
                variant="secondary"
                label={
                  isLower
                    ? t("mobile.workouts.maxDropKeep", {
                        valueKg: c.regression?.currentMaxKg ?? 0,
                      })
                    : t("mobile.workouts.maxBumpDismiss")
                }
                onPress={() => onDismissMaxBump(c.exerciseId)}
                disabled={busy}
                hitSlop={{ top: 8, bottom: 8, left: isLower ? 2 : 8, right: 2 }}
              />
              <Button
                theme={theme}
                compact
                variant="primary"
                loading={busy}
                label={
                  isLower
                    ? t("mobile.workouts.maxDropConfirm", {
                        valueKg: c.suggestion.suggestedKg,
                      })
                    : t("mobile.workouts.maxBumpConfirm")
                }
                onPress={() =>
                  onConfirmMaxBump(c.exerciseId, c.suggestion.suggestedKg, c.direction)
                }
                disabled={busy}
                hitSlop={{ top: 8, bottom: 8, left: 2, right: 8 }}
              />
            </View>
          </View>
        );
      })}
    </View>
  );
}
